import { ReactNode, useRef } from 'react';

import { Box, BoxProps, Flex, Heading, Skeleton, Tag, Text } from '@chakra-ui/react';
import { useResizeObserver } from '@react-hookz/web';

import { CouponAppliedTag } from '~/components/coupon-applied-tag';
import { BillingFrequency } from '~/features/checkout/models/checkout';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { FeatureList } from '~/features/plans/components/feature-list';
import { PriceAndUnitPrice } from '~/features/plans/components/price-and-unit-price';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { PlanMetadata } from '~/features/plans/models/plan-metadata';

import { PlanPriceCardLayout } from './plan-price-card-layout';

export function IndividualPlanCard({
  billingFrequency,
  metadata,
  signUpButtonSlot,
  footnoteSlot,
  setPriceHeight,
  containerProps,
  isBestValueFrameRendered = true,
  syncRenderedPriceHeight = true,
}: {
  billingFrequency: BillingFrequency;
  metadata: PlanMetadata;
  signUpButtonSlot: ReactNode;
  footnoteSlot?: ReactNode;
  /**
   * To align the CTAs across the cards, we need to get the height of the cards.
   */
  setPriceHeight: (newHeight: number) => void;
  containerProps?: BoxProps;
  isBestValueFrameRendered?: boolean;
  /**
   * Boolean value to turn on and off the alignment of the CTAs across the cards.
   */
  syncRenderedPriceHeight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const { currency } = useCurrency();
  const { data: plans } = usePricePlans(currency);
  const {
    displayPrice,
    displayPriceUnits,
    displayPricePerMonth,
    strikeThroughDisplayPrice,
    couponApplied,
    savingsBlurb,
    couponPercentOff,
    strikeThroughDisplayPricePerMonth,
  } = plans?.prices[billingFrequency]?.individual || {};

  useResizeObserver(
    ref,
    (entry) => {
      const height = entry.target.getBoundingClientRect().height;
      setPriceHeight(height);
    },
    syncRenderedPriceHeight
  );

  function renderMonthToMonthPrice() {
    if (!displayPrice && !displayPriceUnits)
      return (
        <Flex flexDirection='column' gap='4px'>
          <Skeleton height='40px' width='120px' />
        </Flex>
      );
    else if (strikeThroughDisplayPrice) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px' size='h2'>
              <Heading
                as='del'
                color='gray.600'
                position='relative'
                size='marketing-2xl'
                textDecor='none'
                sx={{
                  '&::after': {
                    content: '""',
                    backgroundColor: 'gray.600',
                    width: '100%',
                    position: 'absolute',
                    height: '.1em',
                    borderRadius: '.1em',
                    left: '-5%',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    transform: 'translateY(-50%) rotate(-16deg)',
                    top: '50%',
                  },
                }}
              >
                {strikeThroughDisplayPrice}
              </Heading>
            </Heading>
            {couponApplied && typeof couponPercentOff === 'number' && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading as='ins' color='blue.800' fontWeight='500' size='marketing-4xl' textDecor='none'>
            {displayPrice}{' '}
            <Heading as='span' size='marketing-lg'>
              /month
            </Heading>
          </Heading>
        </>
      );
    } else {
      return <PriceAndUnitPrice displayPrice={displayPrice} displayPriceUnits={displayPriceUnits} />;
    }
  }

  function renderBundlePrice() {
    if (!displayPrice && !displayPriceUnits)
      return (
        <Flex flexDirection='column' gap={2}>
          <Skeleton height='40px' width='221px' />
          <Skeleton height='24px' width='231px' />
        </Flex>
      );
    else if (strikeThroughDisplayPricePerMonth) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px' size='h2'>
              <Heading
                as='del'
                color='gray.600'
                position='relative'
                size='marketing-2xl'
                textDecor='none'
                sx={{
                  '&::after': {
                    content: '""',
                    backgroundColor: 'gray.600',
                    width: '100%',
                    position: 'absolute',
                    height: '.1em',
                    borderRadius: '.1em',
                    left: '-5%',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    transform: 'translateY(-50%) rotate(-16deg)',
                    top: '50%',
                  },
                }}
              >
                {strikeThroughDisplayPricePerMonth}
              </Heading>
            </Heading>
            {couponApplied && !!couponPercentOff && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading as='p' color='blue.800' fontWeight='500' size='marketing-4xl'>
            {displayPricePerMonth}{' '}
            <Heading as='span' size='marketing-lg'>
              /month
            </Heading>
          </Heading>
          <Box alignItems='center' as='p' color='gray.900' display='flex' flexWrap='wrap' gap='4px'>
            <Text as='span'>
              for 4-month bundle<Box as='sup'>2</Box>
            </Text>
          </Box>
        </>
      );
    } else {
      return (
        <>
          <Heading as='p' color='blue.800' fontWeight='500' size='marketing-4xl'>
            {displayPricePerMonth}{' '}
            <Heading as='span' size='marketing-lg'>
              /month
            </Heading>
          </Heading>
          <Box alignItems='center' as='p' color='gray.900' display='flex' flexWrap='wrap' gap='4px'>
            <Text as='span'>
              for 4-month bundle<Box as='sup'>2</Box>
            </Text>
          </Box>
        </>
      );
    }
  }

  function renderYearlyPrice() {
    if (!displayPrice && !displayPriceUnits)
      return (
        <Flex flexDirection='column' gap={2}>
          <Skeleton height='40px' width='221px' />
          <Skeleton height='24px' width='231px' />
        </Flex>
      );
    else if (strikeThroughDisplayPricePerMonth) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px' size='h2'>
              <Heading
                as='del'
                color='gray.600'
                position='relative'
                size='marketing-2xl'
                textDecor='none'
                sx={{
                  '&::after': {
                    content: '""',
                    backgroundColor: 'gray.600',
                    width: '100%',
                    position: 'absolute',
                    height: '.1em',
                    borderRadius: '.1em',
                    left: '-5%',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    transform: 'translateY(-50%) rotate(-16deg)',
                    top: '50%',
                  },
                }}
              >
                {strikeThroughDisplayPricePerMonth}
              </Heading>
            </Heading>
            {couponApplied && !!couponPercentOff && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading as='p' color='blue.800' fontWeight='500' size='marketing-4xl'>
            {displayPricePerMonth}{' '}
            <Heading as='span' size='marketing-lg'>
              /month
            </Heading>
          </Heading>
          <Box alignItems='center' as='p' color='gray.900' display='flex' flexWrap='wrap' gap='4px'>
            <Text as='span'>for annual subscription</Text>
          </Box>
        </>
      );
    } else {
      return (
        <>
          <Heading as='p' color='blue.800' fontWeight='500' size='marketing-4xl'>
            {displayPricePerMonth}{' '}
            <Heading as='span' size='marketing-lg'>
              /month
            </Heading>
          </Heading>
          <Box alignItems='center' as='p' color='gray.900' display='flex' flexWrap='wrap' gap='4px'>
            <Text as='span'>for annual subscription</Text>
          </Box>
        </>
      );
    }
  }

  function renderPrice() {
    if (billingFrequency === 'monthly') {
      return renderMonthToMonthPrice();
    } else if (billingFrequency === '4month') {
      return renderBundlePrice();
    } else if (billingFrequency === 'annually') {
      return renderYearlyPrice();
    } else return null;
  }

  const isBestPrice = billingFrequency === '4month' || billingFrequency === 'annually';

  return (
    <PlanPriceCardLayout
      {...containerProps}
      borderColor={isBestValueFrameRendered && isBestPrice ? 'feedback.positive' : 'gray.300'}
    >
      {isBestValueFrameRendered && isBestPrice && (
        <Box
          bgColor='feedback.positive'
          borderTopLeftRadius='8px'
          borderTopRightRadius='8px'
          left='50%'
          p='4px 8px'
          position='absolute'
          top='-32px'
          transform='translateX(-50%)'
          width='90%'
        >
          <Text color='white' fontWeight='600' textAlign='center'>
            Best Value
          </Text>
        </Box>
      )}
      <Flex flex='1' flexDir='column' gap={6}>
        <Flex
          flexDir='column'
          gap={6}
          sx={{
            '&::after': {
              content: "''",
              borderBottom: '1px solid',
              borderBottomColor: 'gray.600',
              opacity: '0.15',
            },
          }}
        >
          <Box flexBasis='var(--max-price-height)' flexGrow='1' flexShrink='0'>
            <Flex ref={ref} flexDir='column' gap={6}>
              <Flex alignItems='flex-start' gap='8px' justifyContent='space-between'>
                <Heading as='p' display='inline-block' size='marketing-xl'>
                  {metadata.title}
                  {billingFrequency === '4month' && (
                    <Heading as='sup' fontFamily='heading' fontSize='xs' fontWeight='500' top='-1em'>
                      1
                    </Heading>
                  )}
                </Heading>

                {!couponApplied && savingsBlurb && (
                  <Tag
                    as='span'
                    bg='blue.400'
                    border='none'
                    color='white'
                    fontFamily='body'
                    fontWeight='600'
                    textTransform='capitalize'
                    whiteSpace='nowrap'
                    fontSize={{
                      base: '2xs',
                      lg: 'xs',
                    }}
                  >
                    {savingsBlurb}
                  </Tag>
                )}
              </Flex>

              <Box>{renderPrice()}</Box>
            </Flex>
          </Box>
          <Box>{signUpButtonSlot}</Box>
        </Flex>
        <Flex flex='1' flexDir='column' gap={4}>
          <FeatureList features={metadata.features} />
          <Box mt='auto'>{footnoteSlot}</Box>
        </Flex>
      </Flex>
    </PlanPriceCardLayout>
  );
}
